import React, { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkspaceContext } from '../providers/WorkspaceProvider';
import { usePreviewContext } from '../providers/PreviewProvider';
import { useAppContext } from '../../../context/AppContext';
import WorkspacePreview from '../preview';
import ATSScreen from '../../../components/ats/ATSScreen';
import JDMatchScreen from '../../../components/jd/JDMatchScreen';
import MockInterviewPanel from '../../../components/MockInterviewPanel';
import CareerRoadmapPanel from '../../../components/CareerRoadmapPanel';
import ApplicationTracker from '../../../components/ApplicationTracker';
import CareerMemoryPanel from '../../../components/CareerMemoryPanel';
import TemplateGallery from '../../../components/gallery';
import VaultWorkspace from '../assets';
import ResumeAuditScreen from '../../../components/audit/ResumeAuditScreen';
import Dashboard from '../../Dashboard';
import ProfilePage from '../../ProfilePage';
import CareerIntelligencePanel from './CareerIntelligencePanel';
import CreateMenu from '../../../components/layout/AppSidebar/actions';

export default function RightWorkspace({ backendStatus }) {
  const navigate = useNavigate();
  const { resumeId } = useParams();

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    activeTab,
    setActiveTab,
    authToken,
    theme,
    toggleTheme,
    handleLogout,
    aiTone,
    setAiTone,
    setCredits,
    setShowCustomize,
    user,
    setUser,
    mobileTab,
  } = useWorkspaceContext();

  const {
    resumeData,
    atsScore,
    template,
    handleTemplateChange,
    accentColor,
    setAccentColor,
    fontScale,
    setFontScale,
    fontFamily,
    setFontFamily,
    credits,
    userDomain,
    setShowDomainSelector,
    coverTemplate,
    setCoverTemplate,
    setInput,
    textareaRef,
    isUploadingResume,
    handleResumeUpload,
    resumeFileInputRef,
    handleCreateVariant,
    handleSelectVariant,
    handleDeleteVariant,
    variants,
    coverLetters,
    handleCreateCoverLetter,
    activeCoverLetter,
  } = usePreviewContext();

  const {
    triggerImportModal,
    handleDeleteDocument,
    setEntryIntent,
  } = useAppContext();

  // State for triggering the CreateMenu wizard from Dashboard cards
  const [dashCreateStep, setDashCreateStep] = useState(null);

  const handleDashboardCreate = useCallback((step) => {
    // 'cover_letter' opens CL wizard; everything else opens resume wizard
    setDashCreateStep(step || 'select');
  }, []);

  // ── Audit state (hoisted here to avoid stale-closure bugs) ──
  const [auditFull, setAuditFull] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');

  const handleRunAudit = useCallback(async () => {
    if (!resumeData || auditLoading) return;
    setAuditLoading(true);
    setAuditError('');
    try {
      const res = await fetch('/api/audit/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        // Backend reads resume from user state — no body needed
      });
      // Backend returns 402 for insufficient credits
      if (res.status === 402 || res.status === 403) {
        const errData = await res.json().catch(() => ({}));
        setAuditError(errData.error || 'Insufficient credits to run audit.');
        return;
      }
      if (!res.ok) {
        setAuditError('Audit failed. Please try again.');
        return;
      }
      const data = await res.json();
      setAuditFull(data.audit || data);
    } catch (err) {
      setAuditError(err.message || 'Audit failed.');
    } finally {
      setAuditLoading(false);
    }
  }, [authToken, resumeData, auditLoading]);

  // handleSendPrompt must be declared before any hook that references it
  const handleSendPrompt = useCallback((promptText) => {
    if (setInput) {
      setInput(promptText);
      const id = resumeId || 'master';
      navigate(`/app/resume/${id}/chat`);
      setTimeout(() => textareaRef?.current?.focus(), 100);
    }
  }, [setInput, navigate, resumeId, textareaRef]);

  // ── JD Match state (hoisted to avoid stale-closure bugs) ──
  const [jdText, setJdText] = useState('');
  const [jdResult, setJdResult] = useState(null);
  const [jdLoading, setJdLoading] = useState(false);
  const [jdError, setJdError] = useState('');
  const [varCreating, setVarCreating] = useState(false);
  const [varCreated, setVarCreated] = useState(false);
  const [deepAnalysis, setDeepAnalysis] = useState(false);

  const handleJDAnalyze = useCallback(async () => {
    if (!jdText.trim() || jdLoading) return;
    setJdLoading(true);
    setJdError('');
    setJdResult(null);
    try {
      const res = await fetch('/api/jd/match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ jd_text: jdText, deep_analysis: deepAnalysis }),
      });
      if (res.status === 402 || res.status === 403) { setJdError('Insufficient credits.'); return; }
      if (!res.ok) { setJdError('Analysis failed. Please try again.'); return; }
      const data = await res.json();
      setJdResult(data);
      if (data.credits_remaining !== undefined && data.credits_remaining !== null) {
        setCredits(data.credits_remaining);
      }
    } catch (err) {
      setJdError(err.message || 'Analysis failed.');
    } finally {
      setJdLoading(false);
    }
  }, [authToken, jdText, jdLoading, deepAnalysis, setCredits]);

  const handleJDOptimize = useCallback(() => {
    if (!jdText.trim()) return;
    handleSendPrompt(`Optimize my resume for this job description:\n\n${jdText}`);
  }, [jdText, handleSendPrompt]);




  const isMobileHidden = resumeData && mobileTab !== 2;

  return (
    <div 
      className={`${isMobileHidden ? 'hidden' : 'flex'} flex-1 flex-col min-w-0 h-full overflow-hidden w-full md:flex`}
    >
      {/* 0. Home Workspace Screen */}
      {activeTab === 'home' && (
        <>
          <Dashboard
            resumeData={resumeData}
            atsScore={atsScore}
            variants={variants}
            coverLetters={coverLetters}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onOpenCustomize={() => setShowCustomize?.(true)}
            isUploadingResume={isUploadingResume}
            handleResumeUpload={handleResumeUpload}
            resumeFileInputRef={resumeFileInputRef}
            credits={credits}
            user={user}
            theme={theme}
            onStartManual={handleCreateVariant}
            onSelectVariant={handleSelectVariant}
            handleDeleteVariant={handleDeleteVariant}
            authToken={authToken}
            onCreateNew={handleDashboardCreate}
            onImportResume={triggerImportModal}
            onDeleteCoverLetter={(clId) => handleDeleteDocument(clId, 'cover_letter')}
            setEntryIntent={setEntryIntent}
          />

          {/* CreateMenu wizard triggered by Dashboard cards (onCreateNew) */}
          {dashCreateStep !== null && (
            <CreateMenu
              externalStep={dashCreateStep}
              onExternalClose={() => setDashCreateStep(null)}
              onImportResume={triggerImportModal}
              theme={theme}
            />
          )}
        </>
      )}

      {/* 1. Resume & Cover Letter Canvas Previews */}
      {((['ai', 'chat', 'resume', 'cover', 'editor', 'templates'].includes(activeTab)) ||
        (['ats', 'jd', 'jd_match'].includes(activeTab) && isMobile)) && (
        <WorkspacePreview />
      )}

      {/* 2. ATS Screen (Desktop only) */}
      {activeTab === 'ats' && !isMobile && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden w-full">
          <ATSScreen
            atsScore={atsScore}
            resumeData={resumeData}
            theme={theme}
            onImprove={(txt) => {
              if (txt && typeof txt === 'string') {
                handleSendPrompt('Fix this ATS issue in my resume: ' + txt);
              } else {
                handleSendPrompt('Help me improve my ATS score. What should I fix first?');
              }
            }}
          />
        </div>
      )}

      {/* 3. JD Matcher Screen (Desktop only) */}
      {['jd_match', 'jd'].includes(activeTab) && !isMobile && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden w-full">
          <JDMatchScreen
            jdText={jdText}
            setJdText={setJdText}
            jdResult={jdResult}
            setJdResult={setJdResult}
            jdLoading={jdLoading}
            jdError={jdError}
            setJdError={setJdError}
            varCreating={varCreating}
            varCreated={varCreated}
            deepAnalysis={deepAnalysis}
            setDeepAnalysis={setDeepAnalysis}
            onAnalyze={handleJDAnalyze}
            onOptimize={handleJDOptimize}
            onCreateVariant={async () => {
              setVarCreating(true);
              try {
                const res = await fetch('/api/variants', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                  body: JSON.stringify({ name: 'JD Tailored Version', jd_text: jdText }),
                });
                if (res.ok) {
                  const d = await res.json();
                  if (d.credits_remaining !== undefined) setCredits(d.credits_remaining);
                  setVarCreated(true);
                  if (d.variant?.id) {
                    navigate(`/app/resume/${d.variant.id}/chat`);
                  }
                }
              } catch {}
              finally { setVarCreating(false); }
            }}
            onCoverLetter={async () => {
              const newCl = await handleCreateCoverLetter();
              if (newCl) {
                navigate(`/app/cover-letter/${newCl.id}`);
              }
            }}
            theme={theme}
          />
        </div>
      )}

      {/* 4. Resume Audit Screen */}
      {activeTab === 'audit' && (
        <ResumeAuditScreen
          auditFull={auditFull}
          auditPreview={null}
          auditLoading={auditLoading}
          auditError={auditError}
          resumeData={resumeData}
          authToken={authToken}
          theme={theme}
          onRunAudit={handleRunAudit}
        />
      )}

      {/* 5. Mock Interview Screen */}
      {activeTab === 'mock_interview' && (
        <MockInterviewPanel
          theme={theme}
          resumeData={resumeData}
          authToken={authToken}
          onSendToChat={handleSendPrompt}
        />
      )}

      {/* 5. Career Roadmap Screen */}
      {activeTab === 'career_roadmap' && (
        <CareerRoadmapPanel
          theme={theme}
          resumeData={resumeData}
          authToken={authToken}
          onSendToChat={handleSendPrompt}
        />
      )}

      {/* 6. Application Tracker Screen */}
      {activeTab === 'tracker' && (
        <div className="flex-1 flex flex-col h-full overflow-hidden">
          <ApplicationTracker authToken={authToken} theme={theme} />
        </div>
      )}

      {/* 7. Career Memory Screen */}
      {activeTab === 'career_memory' && (
        <CareerMemoryPanel
          theme={theme}
          authToken={authToken}
          onCreditsUpdate={(n) => setCredits(n)}
        />
      )}

      {/* 7b. Career Intelligence Panel (Phase 3) */}
      {activeTab === 'career_intelligence' && (
        <div className="flex-1 flex flex-col h-full overflow-y-auto" style={{
          background: theme === 'dark' ? 'var(--surface-1, #0f0f14)' : '#f8fafc',
        }}>
          <CareerIntelligencePanel
            theme={theme}
            onSendPrompt={(prompt) => {
              if (setInput) setInput(prompt);
              if (textareaRef?.current) textareaRef.current.focus();
            }}
          />
        </div>
      )}

      {/* 9. Vault Workspace Screen */}
      {activeTab === 'assets_vault' && (
        <VaultWorkspace />
      )}

      {/* 10. Profile Workspace Screen */}
      {activeTab === 'profile' && (
        <ProfilePage 
          user={user} 
          setUser={setUser}
          theme={theme} 
          toggleTheme={toggleTheme}
          handleLogout={handleLogout}
          aiTone={aiTone}
          setAiTone={setAiTone}
        />
      )}
    </div>
  );
}
